import RiDeleteBinLine from '@ben/icons/delete-bin-line';
import Button, { ButtonVariant } from '@ben/orion/deprecated-button';
import RIcon from '@ben/orion/remix-icon';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import Modal from '@ben/shared/components/modal/modal';
import type { IModalRef } from '@ben/shared/components/modal/modal';
import type { ForwardedRef } from 'react';
import { forwardRef } from 'react';
import styled, { css, useTheme } from 'styled-components';

interface DeleteConfirmationModalProps {
  message: string;
  onConfirm: () => void;
  title: string;
}

const DeleteConfirmationModal = forwardRef(
  (
    { message, onConfirm, title }: DeleteConfirmationModalProps,
    ref: ForwardedRef<IModalRef>
  ) => {
    const theme = useTheme();

    const handleHideModal = () => {
      if (ref && typeof ref !== 'function') {
        ref.current?.hideModal();
      }
    };

    const handleConfirm = () => {
      onConfirm();
      handleHideModal();
    };

    return (
      <Modal ref={ref}>
        <Container>
          <IconContainer>
            <RIcon
              icon={RiDeleteBinLine}
              size="lg"
              color={theme.palette.warning.main}
            />
          </IconContainer>
          <TextContainer>
            <Typography variant={TypographyVariant.h2}>{title}</Typography>
            <Typography variant={TypographyVariant.bodyLg1}>
              {message}
            </Typography>
          </TextContainer>
          <ButtonsContainer>
            <Button
              label="Cancel"
              variant={ButtonVariant.secondary}
              onClick={handleHideModal}
              width="187px"
            />
            <Button
              label="Delete"
              variant={ButtonVariant.error}
              onClick={handleConfirm}
              width="201px"
            />
          </ButtonsContainer>
        </Container>
      </Modal>
    );
  }
);

DeleteConfirmationModal.displayName = 'DeleteConfirmationModal';

export default DeleteConfirmationModal;

const Container = styled.div`
  ${({ theme }) => css`
    width: 492px;
    height: 338px;
    background: ${theme.palette.neutral['00']};
    padding: ${theme.spacing(5)};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: ${theme.borderRadius.md};
  `}
`;

const TextContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-top: ${theme.spacing(2.5)};
    gap: ${theme.spacing(1)};
    white-space: pre-wrap;
  `}
`;

const IconContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 80px;
    height: 80px;
    border-radius: ${theme.borderRadius.full};
    background: ${theme.palette.warning.bg};
  `}
`;

const ButtonsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    margin-top: ${theme.spacing(5)};
  `}
`;
